<template>
    <div class="service-basket flow__panel flow__container-fluid flow__panel--positioned-buttons">
        <div>
            <section>
                <h2>{{ myResources.title }}</h2>
                <p v-if="serviceBasket.hasItems">
                    {{ myResources.introText }}
                </p>
                <p v-else>
                    {{ myResources.emptyBasketText }}
                </p>
            </section>
            <error-handler />
            <section>
                <hh-accent-panel v-if="serviceBasket.hasItems">
                    <basket :service-basket="serviceBasket" @delete="removeServiceProductAsync" hide-continue show-mobile />
                </hh-accent-panel>

                <hh-accent-panel v-if="selectedServiceBasketServices.length && hasVisitedAllRoutes || !serviceBasket.hasItems"
                                 class="service-basket__service-links">
                    <h4>{{ myResources.changeSupportTitle }}</h4>
                    <p>{{ myResources.changeSupportText }}</p>
                    <ul>
                        <li v-for="service in selectedServiceBasketServices" :key="service.key">
                            <router-link :to="{ name: service.routeName }">
                                {{ service.title }}
                            </router-link>
                        </li>
                    </ul>
                </hh-accent-panel>
            </section>
        </div>
        <aside>
            <hh-accent-panel class="service-basket__actions" show-image>
                <h4 class="--not-mobile">
                    {{ myResources.nextStepsTitle }}
                </h4>
                <p class="--not-mobile">
                    {{ myResources.nextStepsText }}
                </p>
                <cta-tile class="service-basket__cta"
                          name="basket-summary"
                          :button-text="commonResources.continue"
                          type="cta"
                          breakout-extended
                          @click="onProceed" />
            </hh-accent-panel>
        </aside>
    </div>
</template>

<script>

import Vuex from 'vuex'

import ProgressManager from '@/progress-manager'
import ServiceBasketManager from '@/service-basket-manager'
import CtaTile from '@/components/app-core/cta-tile'
import ErrorHandler from '@/components/app-core/error-handler'
import ResourcesHelper from '@/components/resources-helper'
import Basket from '@/components/service-basket/basket'
import { MUTATIONS } from '@/store/mutations'
import Project from '@/models/project'

export default {
    mixins: [
        ResourcesHelper,
    ],

    components: {
        Basket,
        CtaTile,
        ErrorHandler,
    },

    async mounted() {
        await ServiceBasketManager.loadBasketAsync()
    },

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {ServiceBasket}
             */
            serviceBasket: stateStore => stateStore.serviceBasket.basket,

            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),
        }),

        /**
         * Return all services which have an available service basket page.
         * @returns {ServiceType[]}
         */
        serviceBasketServices() {
            return this.$scheme.services.filter(service => !!service.routeName)
        },

        /**
         * Returns true if all service basket routes have been visited.
         * @returns {boolean}
         */
        hasVisitedAllRoutes() {
            return ProgressManager.hasVisitedAllRoutes(this.selectedServiceBasketServices.map(s => s.routeName))
        },

        /**
         * Return the resources object for this page.
         * @return {BasketSummaryResources}
         */
        myResources() {
            return this.parseResources('views.serviceBasketSummary')
        },

        selectedServiceBasketServices() {
            const everSelectedOfferings = this.currentProject && this.currentProject.services ? this.currentProject.services : []
            const selectedOfferingKeys = everSelectedOfferings.filter(o => o.value === 'Y').map(o => o.key)
            return this.serviceBasketServices.filter(schemeService => selectedOfferingKeys.includes(schemeService.key))
        },
    },

    methods: {
        /**
         * Remove a service item from the basket.
         * @param {ServiceProduct} item
         */
        async removeServiceProductAsync(item) {
            this.$store.commit(MUTATIONS.clearAllErrors)
            await ServiceBasketManager.removeItemFromBasketAsync(item)
        },

        /**
         * Proceed to the next screen.
         */
        onProceed() {
            this.$router.push(ProgressManager.getNext(this.$route.name))
        },
    },
}

</script>

<style lang="scss">
// NOTE all of the styles are for this page defined in an unscoped style block in home-energy-plan.vue
// which defines the `.service-basket` classes and all derived classes

.service-basket {
    aside .accent-panel {
        margin-top: 2.3rem;
    }

    @include element('actions') {
        .button {
            width: 100%;
        }
    }
}

</style>
